import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../context/AppIdContext';
import '../style/speakSearch.css';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const PhonebookWiseSlipReportDash = () => {
    // --- State ---
    const [volunteers, setVolunteers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    // --- Hooks ---
    const navigate = useNavigate();

    // --- Context ---
    const { appId, language, userId } = useAppId();
    const lang = language || 'en';

    // --- Translation Dictionary ---
    const t = {
        en: {
            pageTitle: "Phonebook Wise Slip Sending Report",
            filterPlaceholder: "Search by name or mobile...",
            loadingData: "Loading Data...",
            noData: "No volunteers found.",
            call: "Call",
            members: "Members :",
            boothWiseBtn: "Booth Wise",
            totalSum: "Total Members :" // New Label
        },
        hi: {
            pageTitle: "फोनबुक वार स्लिप भेजने की रिपोर्ट",
            filterPlaceholder: "नाम या मोबाइल द्वारा खोजें...",
            loadingData: "डेटा लोड हो रहा है...",
            noData: "कोई स्वयंसेवक नहीं मिला।",
            call: "कॉल",
            members: "सदस्य :",
            boothWiseBtn: "बूथ वार",
            totalSum: "कुल सदस्य :" // New Label
        },
        gu: {
            pageTitle: "ફોનબુક વાઈઝ સ્લિપ મોકલવાનો રિપોર્ટ",
            filterPlaceholder: "નામ અથવા મોબાઇલથી શોધો...",
            loadingData: "ડેટા લોડ થઈ રહ્યો છે...",
            noData: "કોઈ સ્વયંસેવક મળ્યા નથી.",
            call: "કૉલ",
            members: "સભ્યો :",
            boothWiseBtn: "બૂથ વાઈઝ",
            totalSum: "કુલ સભ્યો :"
        }
    };

    const txt = t[lang];

    // --- API Fetch Logic ---
    const fetchVolunteers = async () => {
        if (!appId || !userId) return;

        setLoading(true);
        setSearchTerm("");

        try {
            const response = await axios.post(
                `${BASE_URL}/api/dis_all_contact_match_admin`,
                {
                    app_id: appId,
                    user_id: userId
                }
            );

            if (response.data.success && Array.isArray(response.data.data.result)) {
                setVolunteers(response.data.data.result);
            } else {
                setVolunteers([]);
            }
        } catch (error) {
            console.error("Error fetching volunteers:", error);
            setVolunteers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVolunteers();
    }, [appId, userId]);

    // --- Filter Logic ---
    const filteredVolunteers = volunteers.filter((vol) => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();

        const name = vol.name ? vol.name.toLowerCase() : "";
        const mobile = vol.mobile_no ? String(vol.mobile_no) : "";
        const desig = vol.designation ? vol.designation.toLowerCase() : "";

        return name.includes(term) || mobile.includes(term) || desig.includes(term);
    });

    // --- Calculate Total Members ---
    const totalMemberCount = filteredVolunteers.reduce((sum, vol) => {
        return sum + (parseInt(vol.total_phonebook_member) || 0);
    }, 0);

    // --- Navigation Handlers ---
    const handleBoothWiseClick = () => {
        navigate('/dis_booth_wise_phonebook');
    };

    const handleCardClick = (selectedUserId) => {
        navigate('/phonebook_wise_slip_sending', { state: { userId: selectedUserId } });
    };

    return (
        <div className='app-container p-4'>
            {/* Page Header */}
            <div className="page-header mb-4">
                <div className="header-content d-flex justify-content-between align-items-center w-100 flex-wrap gap-2">
                    <div>
                        <h1 className="page-title-text">{txt.pageTitle}</h1>
                        {!loading && (
                            <div className="d-flex align-items-center gap-3 flex-wrap text-muted mt-2 mb-0" style={{ fontSize: '0.95rem', fontWeight: '500' }}>
                                {/* Contacts Found Count */}
                                <span>
                                    <span className="count-badge">{filteredVolunteers.length}</span> {lang === 'en' ? 'contacts' : 'संपर्क'}
                                </span>

                                <span style={{ color: '#cbd5e1' }}>|</span>

                                {/* NEW: Total Members Sum */}
                                <span>


                                    {txt.totalSum}<span className="count-badge" style={{ backgroundColor: '#dcfce7', color: '#166534' }}>{totalMemberCount}</span>
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Search Filter & Button */}
            {volunteers.length > 0 && (
                <div className="filter-container mb-4">
                    <div className="search-row-flex">
                        <div className="search-wrapper">
                            <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input
                                className="search-input"
                                type="text"
                                placeholder={txt.filterPlaceholder}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Loading Spinner */}
            {loading && (
                <div className="loader-wrapper">
                    <div className="spinner"></div>
                    <p className="loader-text">{txt.loadingData}</p>
                </div>
            )}

            {/* No Data State */}
            {!loading && filteredVolunteers.length === 0 && (
                <div className="text-center text-muted p-5 empty-state">
                    <div className="empty-icon">👥</div>
                    <p>{txt.noData}</p>
                </div>
            )}

            {/* --- VOLUNTEER LIST GRID --- */}
            <div className="row g-4 pb-5">
                {!loading && filteredVolunteers.map((vol, index) => {
                    const uniqueKey = vol.user_id ? `vol-${vol.user_id}` : `vol-${index}`;

                    const userTypeLabel = vol.designation || (vol.user_type === 'A' ? 'Admin' : (vol.user_type === 'k' ? 'Karyakarta' : 'User'));

                    // Badge Color Logic
                    let badgeClass = 'badge-blue';
                    if (vol.user_type === 'A' || userTypeLabel.toLowerCase().includes('admin') || userTypeLabel.includes('एडमिन')) {
                        badgeClass = 'badge-red';
                    } else if (vol.user_type === 'k' || userTypeLabel.toLowerCase().includes('karya') || userTypeLabel.includes('कार्यकर्त्ता')) {
                        badgeClass = 'badge-orange';
                    }

                    // Avatar Color Logic
                    let avatarBg = '#f3f4f6'; let avatarText = '#6b7280';
                    if (badgeClass === 'badge-red') { avatarBg = '#fee2e2'; avatarText = '#dc2626'; }
                    else if (badgeClass === 'badge-orange') { avatarBg = '#fef3c7'; avatarText = '#d97706'; }
                    else { avatarBg = '#e0f2fe'; avatarText = '#0284c7'; }

                    // Member Count
                    const memberCount = vol.total_phonebook_member || 0;

                    return (
                        <div className="col-12 col-lg-3" key={uniqueKey}>
                            <div
                                className="volunteer-card-web clickable-card"
                                onClick={() => handleCardClick(vol.user_id)}
                            >
                                {/* BOOTSTRAP GRID SYSTEM APPLIED INSIDE CARD */}
                                <div className="row align-items-center g-3">

                                    {/* Col 1: Avatar */}
                                    <div className="col-auto">
                                        <div className="card-avatar-wrapper">
                                            {vol.photo_path ? (
                                                <img src={vol.photo_path} alt="user" className="card-avatar-img" onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex' }} />
                                            ) : null}
                                            <div className="card-avatar-placeholder" style={{ display: vol.photo_path ? 'none' : 'flex', backgroundColor: avatarBg, color: avatarText }}>
                                                {vol.name ? vol.name.charAt(0).toUpperCase() : "U"}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Col 2: Info (Name, Role, Member Count) */}
                                    <div className="col">
                                        <div className="card-middle">
                                            <h3 className="card-title-name text-truncate" title={vol.name}>
                                                {index + 1}. {vol.name || "Unknown"}
                                            </h3>

                                            <div className="card-meta-row d-flex align-items-center flex-wrap gap-2">
                                                <span className={`role-badge ${badgeClass}`}>
                                                    {userTypeLabel}
                                                </span>

                                                <span className="divider-dot">•</span>

                                                {/* Member Count */}
                                                <span className="member-count-label">
                                                    {txt.members} <strong>{memberCount}</strong>
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Col 3: Action Button (Auto Width) */}
                                    <div className="col-auto">
                                        <button
                                            className="btn-call-circle"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                window.open(`tel:${vol.mobile_no}`);
                                            }}
                                            disabled={!vol.mobile_no}
                                            title={txt.call}
                                        >
                                            <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
                                            </svg>
                                        </button>
                                    </div>

                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <style jsx>{`
                /* --- Common Styles --- */
                .page-title-text { font-size: 1.8rem; font-weight: 800; color: #1e293b; background: linear-gradient(135deg, #1e293b 0%, #334155 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
                .count-badge { background: #e2e8f0; color: #475569; padding: 2px 8px; border-radius: 12px; font-weight: 700; font-size: 0.85rem; }
                .btn-back { background-color: white; color: #64748b; border: 1px solid #e2e8f0; padding: 8px 16px; border-radius: 8px; font-weight: 600; cursor: pointer; display: flex; align-items: center; transition: all 0.2s; }
                .btn-back:hover { background-color: #f8fafc; color: #334155; border-color: #cbd5e1; }
                .search-wrapper-modern { position: relative; background: white; border-radius: 16px; box-shadow: 0 4px 20px -5px rgba(0, 0, 0, 0.05); border: 1px solid #f1f5f9; }
                .search-icon-modern { position: absolute; left: 16px; top: 50%; transform: translateY(-50%); width: 20px; height: 20px; color: #94a3b8; }
                .search-input-modern { width: 100%; padding: 16px 16px 16px 48px; border: none; background: transparent; font-size: 1rem; color: #334155; outline: none; border-radius: 16px; }
                
                /* --- CARD STYLES --- */
                .volunteer-card-web {
                    background: white;
                    border-radius: 12px;
                    padding: 16px; 
                    box-shadow: 0 2px 5px rgba(0,0,0,0.05);
                    border: 1px solid #e5e7eb;
                    transition: transform 0.2s, box-shadow 0.2s;
                    cursor: pointer;
                }
                
                .volunteer-card-web:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 8px 15px -3px rgba(0,0,0,0.1);
                    border-color: #cbd5e1;
                }

                /* Avatar Wrappers */
                .card-avatar-wrapper {
                    width: 56px;
                    height: 56px;
                }
                .card-avatar-img, .card-avatar-placeholder {
                    width: 100%;
                    height: 100%;
                    border-radius: 50%;
                    object-fit: cover;
                    border: 2px solid white;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                }
                .card-avatar-placeholder {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.5rem;
                    font-weight: 700;
                }

                /* Content */
                .card-title-name {
                    font-size: 1.1rem;
                    font-weight: 700;
                    color: #1e293b;
                    margin: 0 0 4px 0;
                }

                /* Badges */
                .role-badge {
                    font-size: 0.7rem;
                    font-weight: 700;
                    padding: 3px 8px;
                    border-radius: 4px;
                    color: white;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
                .badge-red { background-color: #ef4444; }
                .badge-orange { background-color: #f59e0b; }
                .badge-blue { background-color: #3b82f6; }

                /* Member Count Text */
                .member-count-label {
                    font-size: 0.85rem;
                    color: #4b5563;
                    font-weight: 500;
                }
                .member-count-label strong {
                    color: #1e293b;
                    font-weight: 700;
                }
                .divider-dot {
                    color: #cbd5e1;
                    font-weight: bold;
                }

                /* Call Button */
                .btn-call-circle {
                    width: 42px;
                    height: 42px;
                    border-radius: 50%;
                    background-color: #e0f2fe; 
                    color: #0284c7;
                    border: none;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .btn-call-circle:hover {
                    background-color: #0ea5e9;
                    color: white;
                    transform: scale(1.1);
                }
                .btn-call-circle:disabled {
                    background-color: #f3f4f6;
                    color: #d1d5db;
                    cursor: not-allowed;
                }

                /* Flex Container for Search and Button */
                .search-row-flex {
                    display: flex;
                    gap: 12px;
                    align-items: center;
                    width:100%;
                }
                .search-wrapper {
                    flex-grow: 1; 
                }
                .btn-booth-wise {
                    background-color: #3b82f6;
                    color: white;
                    padding: 0 20px;
                    height: 48px;
                    border-radius: 8px;
                    border: none;
                    font-weight: 600;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: background-color 0.2s;
                    font-size: 0.95rem;
                    white-space: nowrap;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
                }
                .btn-booth-wise:hover {
                    background-color: #2563eb;
                }

                /* Loader Styles */
                .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 60vh; width: 100%; }
                .spinner { width: 50px; height: 50px; border: 4px solid #E5E7EB; border-top: 4px solid #2563EB; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 16px; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); }
                .loader-text { color: #6B7280; font-weight: 500; font-size: 1.1rem; animation: pulse 1.5s ease-in-out infinite; }
                .empty-state { background: #f8fafc; border-radius: 20px; border: 2px dashed #e2e8f0; }
                .empty-icon { font-size: 3rem; margin-bottom: 10px; opacity: 0.5; }
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
            `}</style>
        </div>
    );
};

export default PhonebookWiseSlipReportDash;